import React, { useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { Form, FormikHelpers, FormikProps } from "formik";
import Select from "react-select";
import { ReactComponent as IconEstadisticas } from "../../../assets/img/icon/dashboard/estadisticas.svg";
import BlkFormik from "../../../coms/BlkFormik";
import { BlkField } from "../../../coms/BlkField";
import { ComboCliente } from "../../ComboCliente";
import { initialPolicyValues, policyExcelSchema } from "../../../vam/bi/schemas/policy-excel.schema";
import { CubeBasicFilters, SelectOption } from "../schemas/kpi.types";
import { useCubePolicy } from "./useCubePolicy";
import { getUserPolicy } from "../../../vam/bi/components/usePolicyGenUser";
import { useUser } from "../../../custom-hooks/useUser";
import { PolicyFormValues, PolicyValidations } from "../../../vam/bi/schemas/policy.types";
import { Alert } from "../../../interface/alert.interface";

interface StatisticsFiltersProps {
    alertRef: React.RefObject<Alert | undefined>;
    onFiltersChange: React.Dispatch<React.SetStateAction<CubeBasicFilters>>;
}

export const StatisticsFilters: React.FC<StatisticsFiltersProps> = ({ alertRef, onFiltersChange }) => {
    const user = useUser();

    const { policyOptions, loading, fetchData } = useCubePolicy(user, alertRef);

    const buildQuery = getUserPolicy(user.isAdmin);
    const { isAdmin, isEjecutivo, cdCliente: userClientCode } = user;
    const [validity, setValidity] = useState<PolicyValidations[]>([]);

    const needsClientSelector = isAdmin || isEjecutivo;

    return (
        <Row className="my-4 align-items-center">
            <Col lg="auto" className="d-flex align-items-center">
                <div className="icon-wrapper">
                    <IconEstadisticas />
                </div>
                <div className="mx-3">
                    <p className="h4 mb-0">
                        Estadísticas de <span style={{ color: "#006465" }}>siniestralidad</span>
                    </p>
                </div>
            </Col>

            <Col>
                <BlkFormik
                    initialValues={{
                        ...initialPolicyValues,
                        cdCliente: needsClientSelector ? "" : userClientCode,
                    }}
                    validationSchema={policyExcelSchema}
                    onSubmit={(values: PolicyFormValues, actions: FormikHelpers<PolicyFormValues>) => {
                        setValidity([]);
                        onFiltersChange(() => ({}));
                        const clientCode = values.cdCliente ? Number(values.cdCliente) : null;
                        const idClient = needsClientSelector ? clientCode : userClientCode;
                        fetchData(buildQuery(idClient), actions);
                    }}
                >
                    {({ setFieldValue, values }: FormikProps<PolicyFormValues>) => (
                        <Form>
                            <div className="d-flex flex-wrap align-items-end gap-2">
                                {needsClientSelector ? (
                                    <>
                                        <div style={{ flex: 1 }}>
                                            <ComboCliente
                                                setFieldValue={setFieldValue}
                                                name="cdCliente"
                                                alert={alertRef}
                                                values={values}
                                                typePerson={2}
                                            />
                                        </div>
                                        <Button type="submit" className="btn__yellow" disabled={loading}>
                                            Buscar
                                        </Button>
                                    </>
                                ) : (
                                    <Button type="submit" size="sm" className="btn__yellow ms-3" disabled={loading}>
                                        Cargar mis pólizas
                                    </Button>
                                )}
                                {policyOptions.length > 0 && (
                                    <div style={{ flex: 2 }}>
                                        <BlkField
                                            component="select"
                                            className="form-control inputTable w-100"
                                            name="polizaSelect"
                                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                                const policyField = event.target.value;
                                                void setFieldValue("polizaSelect", policyField);

                                                const selectedPolicy = policyOptions.find(
                                                    item => `${item.policy}-${item.idInsuranceCompany}` === policyField
                                                );

                                                setValidity(selectedPolicy?.policyValidations || []);
                                                void setFieldValue("vigenciaSelect", "");

                                                onFiltersChange(filters => ({
                                                    ...filters,
                                                    idClient: values.cdCliente ? Number(values.cdCliente) : undefined,
                                                    policy: selectedPolicy ? selectedPolicy.policy : undefined,
                                                    idQuotes: undefined,
                                                }));
                                            }}
                                        >
                                            <option value="">-- Poliza --</option>
                                            {policyOptions.map(item => (
                                                <option
                                                    key={`${item.policy}-${item.idInsuranceCompany}`}
                                                    value={`${item.policy}-${item.idInsuranceCompany}`}
                                                >
                                                    {`${item.policy} - ${item.insuranceCompanyName}`}
                                                </option>
                                            ))}
                                        </BlkField>
                                    </div>
                                )}

                                {validity.length > 0 && (
                                    <div className="vigencias-select">
                                        <Select
                                            isMulti
                                            name="vigenciaSelect"
                                            placeholder="-- Vigencias --"
                                            classNamePrefix="react-select"
                                            options={validity.map(validityPolicy => ({
                                                value: Number(validityPolicy.idQuotation),
                                                label: validityPolicy.labelInformation,
                                            }))}
                                            value={(values.vigenciaSelect || [])
                                                .map(validityValue => {
                                                    const vigencia = validity.find(
                                                        policyValidation =>
                                                            Number(policyValidation.idQuotation) ===
                                                            Number(validityValue)
                                                    );
                                                    return vigencia
                                                        ? {
                                                              value: Number(vigencia.idQuotation),
                                                              label: vigencia.labelInformation,
                                                          }
                                                        : null;
                                                })
                                                .filter(
                                                    (selectedOption): selectedOption is SelectOption =>
                                                        selectedOption !== null
                                                )}
                                            onChange={(selectedOptions: SelectOption[]) => {
                                                const selectedValues = selectedOptions
                                                    ? selectedOptions.map(option => option.value)
                                                    : [];

                                                setFieldValue("vigenciaSelect", selectedValues);

                                                onFiltersChange(filters => ({
                                                    ...filters,
                                                    policy: filters.policy,
                                                    idClient: values.cdCliente
                                                        ? Number(values.cdCliente)
                                                        : filters.idClient,
                                                    idQuotes: selectedValues,
                                                }));
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </Form>
                    )}
                </BlkFormik>
            </Col>
        </Row>
    );
};
